import React, { useEffect, useRef, useState } from "react";
import { CustomerStatus } from "@models/Customer";
import { DivisionStatus } from "@models/DivisionStatus";
import styled from "styled-components";
import { colors } from "@theme/theming";
import { ActionButton, ButtonsContainer, QueryError } from "@components";
import { QueryState, useAsyncCommand, useDialog, useServices } from "@hooks";
import { Alert } from "react-bootstrap";
import ToastInfo from "./ToastInfo";
import DatePicker from "./forms/DatePicker";
import { format } from "date-fns";
import { DateString } from "@util/types";

type StatusToggleProps = { targetId: string; onSuccess: () => void } & (
	| { target: "customer"; curStatus: CustomerStatus; targetName?: string }
	| { target: "division"; curStatus: DivisionStatus; targetName?: string }
);

type StatusProps = {
	status: CustomerStatus | DivisionStatus;
};

const StatusMap = {
	customer: CustomerStatus,
	division: DivisionStatus,
};

const getStatusColor = (status: StatusProps["status"]): string => {
	switch (status) {
		case CustomerStatus.Trial:
		case DivisionStatus.Trial:
			return colors.info.i300;
		case CustomerStatus.Active:
		case DivisionStatus.Active:
			return colors.success.s300;
		case CustomerStatus.Deactivated:
		case DivisionStatus.Deactivated:
			return colors.gray.g300;
		case CustomerStatus.Archived:
		case DivisionStatus.Archived:
			return colors.black;
	}
};

export const getStatusText = (status: StatusProps["status"]): string => {
	switch (status) {
		case CustomerStatus.Trial:
		case DivisionStatus.Trial:
			return "Trial";
		case CustomerStatus.Active:
		case DivisionStatus.Active:
			return "Aktiv";
		case CustomerStatus.Deactivated:
		case DivisionStatus.Deactivated:
			return "Deaktiviert";
		case CustomerStatus.Archived:
		case DivisionStatus.Archived:
			return "Archiviert";
	}
};

export const getStatusAction = (
	status: StatusProps["status"]
): { doing: string; do: string } => {
	switch (status) {
		case CustomerStatus.Trial:
		case DivisionStatus.Trial:
			throw new Error(`Unexpected status: ${status}`);
		case CustomerStatus.Active:
		case DivisionStatus.Active:
			return { doing: "aktiviere", do: "aktivieren" };
		case CustomerStatus.Deactivated:
		case DivisionStatus.Deactivated:
			return { doing: "deaktiviere", do: "deaktivieren" };
		case CustomerStatus.Archived:
		case DivisionStatus.Archived:
			return { doing: "archiviere", do: "archivieren" };
	}
};

export const StatusToggle = (props: StatusToggleProps) => {
	const { target, targetId, targetName, curStatus, onSuccess } = props;
	const targetStatus = StatusMap[target];

	const { showConfirmDialog } = useDialog<{ targetDate: DateString }>();
	const targetDateRef = useRef<DateString>(
		format(new Date(), "yyyy-MM-dd") as DateString
	);

	const [showLongOperationHint, setShowLongOperationHint] = useState(false);
	const [submittedStatus, setSubmittedStatus] = useState<
		CustomerStatus | DivisionStatus | null
	>(null);

	const { customerService, divisionService } = useServices();
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [updateTargetStatus, queryState, _, resetQueryState] = useAsyncCommand(
		async ({ status, targetDate }) => {
			setSubmittedStatus(status);
			setShowLongOperationHint(true);

			switch (target) {
				case "customer":
					await customerService.updateCustomerStatus(
						targetId,
						status as CustomerStatus
					);
					break;
				case "division":
					await divisionService.updateDivisionStatus(
						targetId,
						status as DivisionStatus,
						targetDate as DateString
					);
					break;
			}

			onSuccess();
		}
	);

	// reset isLoading only after onSuccess updates the entity
	useEffect(() => {
		setSubmittedStatus(null);
		setShowLongOperationHint(false);
		resetQueryState();
	}, [curStatus]);

	const handleStatusButtonPress = (
		status: StatusProps["status"],
		label: string
	) => {
		showConfirmDialog({
			onConfirm: () =>
				updateTargetStatus({ status, targetDate: targetDateRef.current }),
			confirmText: `${targetDE} ${getStatusAction(status).do}`,
			title: label.replace("Kunde", "Kunden"),
			payload: { targetDate: targetDateRef.current },
			content: ({ setSubmitDisabled }) => (
				<>
					Sind Sie sicher, dass Sie den {label.replace("Kunde", "Kunden")}{" "}
					wollen?
					{target === "division" && status === DivisionStatus.Deactivated ? (
						<DivisionDeactivationWarning
							setSubmitDisabled={setSubmitDisabled}
							targetName={props.targetName ?? ""}
							setDeactivationDate={(date) => {
								if (date) {
									targetDateRef.current = date;
								}
							}}
						/>
					) : (
						<></>
					)}
				</>
			),
		});
	};

	const targetDE = target === "customer" ? "Kunde" : "Standort";

	const StatusButton = ({ status }: StatusProps) => {
		const submittingThisStatus = status === submittedStatus;
		const action = getStatusAction(status);
		const statusLabel = submittingThisStatus
			? `${action.doing} ${targetDE}`
			: `${targetDE} ${action.do}`;

		return (
			<StyledActionButton
				key={`${targetId}-${status}`}
				status={status}
				query={submittingThisStatus ? queryState : undefined}
				variant="secondary"
				disabled={queryState.state !== "idle" && queryState.state !== "error"}
				onClick={() =>
					handleStatusButtonPress(
						status,
						`${targetDE} '${targetName}' ${action.do}`
					)
				}
			>
				{statusLabel}
			</StyledActionButton>
		);
	};

	return (
		<StatusToggleContainer>
			<ToastInfo
				isVisible={showLongOperationHint}
				onClose={() => {
					setShowLongOperationHint(false);
				}}
				message="Dies kann einige Zeit dauern."
			/>
			{[
				targetStatus.Trial,
				targetStatus.Deactivated,
				targetStatus.Archived,
			].includes(curStatus) && <StatusButton status={targetStatus.Active} />}

			{curStatus === targetStatus.Active && (
				<StatusButton status={targetStatus.Deactivated} />
			)}

			{curStatus === targetStatus.Deactivated && (
				<StatusButton status={targetStatus.Archived} />
			)}
			<QueryError queryState={queryState} />
		</StatusToggleContainer>
	);
};

const DivisionDeactivationWarning = ({
	targetName,
	setSubmitDisabled,
	setDeactivationDate,
}: {
	setSubmitDisabled: (disabled: boolean) => void;
	targetName: string;
	setDeactivationDate: (date: DateString) => void;
}): JSX.Element => {
	const [nameInput, setNameInput] = useState("");
	const [dateInput, setDateInput] = useState(new Date());

	useEffect(() => {
		setSubmitDisabled(nameInput !== targetName);
	}, [nameInput, targetName]);

	return (
		<>
			<Alert variant="primary" style={{ marginBottom: 0, marginTop: 19 }}>
				<b>Achtung:</b> Sie sind dabei, einen Standort zu deaktivieren.
				<br />
				Bitte stellen Sie sicher, dass Sie alle relevanten Informationen und
				Daten gesichert haben, bevor Sie den Vorgang fortsetzen.
			</Alert>
			<br />
			Nach der Deaktivierung werden die folgenden Maßnahmen durchgeführt:
			<ol style={{ marginTop: 5 }}>
				<li>
					Alle nicht beendeten Benutzer werden zum angegebenen Beendigungsdatum
					beendet.
				</li>
				<li>
					Alle aktiven Modul-Einstellungen und Dauerbelege werden zum
					angegebenen Beendigungsdatum beendet.
				</li>
				<li>
					Alle zukünftigen Modul-Einstellungen und Dauerbelege werden gelöscht.
				</li>
				<li>
					Alle Preiseinstellungen werden zum angegebenen Beendigungsdatum
					beendet.
				</li>
				<li>Alle Benutzer, die in der Zukunft beginnen, werden gelöscht.</li>
				<li>Die JobRad Integration wird gelöscht.</li>
			</ol>
			Nach der Deaktivierung des Standorts kann der Kunde{" "}
			<b>keine Änderungen mehr vornehmen</b> und{" "}
			<b>keine neuen Einträge erstellen</b>, weder über das Portal noch über die
			öffentliche API, einschließlich:
			<ul style={{ marginTop: 5 }}>
				<li>Benutzer</li>
				<li>Modul-Einstellungen</li>
				<li>Report-Einstellungen</li>
				<li>Neue Integrationen</li>
			</ul>
			<Alert variant="danger" style={{ marginBottom: 0 }}>
				<b>Wichtiger Hinweis:</b> Die Deaktivierung ist ein irreversibler
				Vorgang. Alle oben genannten Aktionen können <strong>nicht</strong>{" "}
				rückgängig gemacht werden.
			</Alert>
			<br />
			<p>
				<p>
					<b>Was bedeutet das Beendigungsdatum?</b>
				</p>
				Alle Nutzer dieses Standorts werden zum angegebenen Datum deaktiviert.
				Der Standort an sich wird allerdings sofort deaktiviert, auch, wenn das
				Beendigungsdatum in der Zukunft liegt.
			</p>
			<b>Beendigungsdatum</b>
			<br />
			<DatePicker
				value={dateInput}
				onDayChange={(date) => {
					const formattedDate = format(date, "yyyy-MM-dd") as DateString;
					console.log("Formatted date:", formattedDate);
					setDateInput(date);
					setDeactivationDate(formattedDate);
				}}
				placeholder="TT.MM.JJJJ"
			/>
			<br />
			<br />
			<p>
				Bitte geben Sie den Namen des Standorts (<b>{targetName}</b>) ein, um
				die Deaktivierung zu bestätigen:
			</p>
			<input
				type="text"
				value={nameInput}
				onChange={(e) => setNameInput(e.target.value)}
				placeholder="Name des Standorts"
				style={{
					width: "100%",
					padding: "0.5rem",
					borderRadius: "5px",
					border: `1px solid ${colors.gray.g300}`,
				}}
			/>
		</>
	);
};

const StatusToggleContainer = styled(ButtonsContainer)`
	width: min-content;
`;

const StyledActionButton = styled(ActionButton)<{
	status: CustomerStatus | DivisionStatus;
	query: QueryState<any>;
}>`
	width: max-content;

	${({ query, status }) =>
		query === undefined || query.state === "idle"
			? `
			color: ${getStatusColor(status)};
			border-color: ${getStatusColor(status)};
			background: ${colors.white};

			&:hover {
			  color: ${colors.white};
			  background-color: ${getStatusColor(status)};
			  border-color: ${getStatusColor(status)};
			}
		  `
			: ""}
`;

export const StatusSpan = ({ status }: StatusProps) => (
	<StatusSpanContainer status={status}>
		{getStatusText(status)}
	</StatusSpanContainer>
);

const StatusSpanContainer = styled.span<{
	status: CustomerStatus | DivisionStatus;
}>`
	display: flex;
	gap: 1ch;
	width: min-content;
	font-size: 0.8rem;
	padding: 5px;
	font-weight: initial;
	background: ${colors.white};
	border: 1px solid ${colors.gray.g300};
	border-radius: 5px;
	color: ${({ status }) => getStatusColor(status)};
	border-color: ${({ status }) => getStatusColor(status)};
	text-transform: uppercase;
	min-height: 2rem;
`;
